import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/lang";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await api.login({ email, password });
      localStorage.setItem("fortis_token", result.token);
      localStorage.setItem("fortis_email", result.email);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(t("admin_invalid"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-6" data-testid="admin-login-page">
      {/* Background orbs */}
      <div className="orb w-[400px] h-[400px] bg-[#4D00FF]/15 -top-20 -right-20" />
      <div className="orb w-[300px] h-[300px] bg-[#00F0FF]/10 bottom-20 -left-20" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <button
          onClick={() => navigate("/")}
          data-testid="admin-back-home"
          className="flex items-center gap-2 text-[#A0A0AB] hover:text-[#00F0FF] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> {t("admin_back")}
        </button>

        <div className="glass-card rounded-3xl p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[#00F0FF]/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-[#00F0FF]" />
            </div>
            <div>
              <h1 className="font-['Unbounded'] text-2xl font-bold text-white">{t("admin_title")}</h1>
              <p className="text-[#A0A0AB] text-sm">{t("admin_subtitle")}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="block text-sm text-[#A0A0AB] mb-2">{t("admin_email")}</label>
              <input
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 transition-all"
                placeholder="liza@fortisdigital.com"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A0A0AB] mb-2">{t("admin_password")}</label>
              <input
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 transition-all"
                placeholder="Enter password"
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm" data-testid="admin-login-error">{error}</p>
            )}

            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="neon-btn w-full text-center py-4 text-base disabled:opacity-50"
            >
              {loading ? t("admin_signing") : t("admin_signin")}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
